<template>
	<div class="media-preview-root">
		<div class="media-preview-files d-flex flex-wrap">
			<v-hover v-for="(file, i) in files" :key="i" v-slot:default="{ hover }">
				<v-card :elevation="hover ? 6 : 2" class="media-preview-file-card mr-3 mb-3" max-width="280">
					<v-btn
						v-show="hover"
						absolute
						fab
						x-small
						top
						right
						@click="removeFile(i)"
						:style="{ right: '-12px !important' }"
						class="elevation-4"
						color="white"
					>
						<v-icon small color="grey darken-1">close</v-icon>
					</v-btn>

					<v-list-item three-line class="px-4 py-3">
						<v-list-item-content>
							<div class="overline mb-1 primary--text">Selected file</div>
							<v-list-item-title class="mb-1 text-body-2 font-weight-medium text-truncate">{{
								file.name
							}}</v-list-item-title>
							<v-list-item-subtitle class="text-caption">{{ getFileSizeInMB(file.size) }}</v-list-item-subtitle>
						</v-list-item-content>

						<v-avatar tile size="48" class="media-preview-avatar">
							<v-icon color="primary" size="28">insert_drive_file</v-icon>
						</v-avatar>
					</v-list-item>
				</v-card>
			</v-hover>
			<label v-if="showAddIcon" class="media-preview-dropzone">
				<input
					:data-cy="dataCy"
					class="media-preview-dropzone__input"
					:multiple="multiple"
					type="file"
					accept="audio/*, video/*, image/*, zip/*, application/*"
					@change="onFileChange"
					ref="input"
				/>
				<v-icon size="36" color="primary" class="mb-2">cloud_upload</v-icon>
				<span class="media-preview-dropzone__title">Add files</span>
				<span class="media-preview-dropzone__sub">Images, PDF, audio or video · max 20 MB each</span>
			</label>
		</div>
	</div>
</template>

<script>
export default {
	name: "MediaPreview",
	props: {
		multiple: {
			default: false
		},
		maxFileSize: {
			default: 20 * 1024 * 1024 // 20 mb
		},
		dataCy: {
			default: ""
		}
	},
	data() {
		return {
			files: []
		};
	},
	methods: {
		getFileSizeInMB(byte) {
			return `${Number(byte / (1024 * 1024)).toFixed(2)} MB`;
		},
		onFileChange(e) {
			if (this.maxFileSize) {
				const files = Array.from(e.target.files).reduce(
					(acc, file) => {
						if (file.size >= this.maxFileSize) {
							return {
								...acc,
								greaterThanMaxFileSize: [file, ...acc.greaterThanMaxFileSize]
							};
						} else {
							return {
								...acc,
								lessThanMaxFileSize: [file, ...acc.lessThanMaxFileSize]
							};
						}
					},
					{
						greaterThanMaxFileSize: [],
						lessThanMaxFileSize: []
					}
				);
				if (files.greaterThanMaxFileSize.length) {
					this.$store.dispatch("SHOW_ALERT", {
						text: `${files.greaterThanMaxFileSize
							.map(file => file.name)
							.join(", ")} have file size greater than 20mb`
					});
				}
				if (files.lessThanMaxFileSize.length) {
					this.files = [...files.lessThanMaxFileSize, ...this.files];
				}
			} else {
				this.files = [...Array.from(e.target.files), ...this.files];
			}

			this.$refs.input.value = "";
		},
		removeFile(index) {
			this.files.splice(index, 1);
		}
	},
	computed: {
		showAddIcon() {
			if (this.multiple) {
				return true;
			}
			if (this.files.length === 1) {
				return false;
			}
			return true;
		}
	},
	watch: {
		files(newVal) {
			this.$emit("change", newVal);
		}
	}
};
</script>

<style lang="scss" scoped>
.media-preview-root {
	width: 100%;
}

.media-preview-file-card {
	border-radius: 12px !important;
	border: 1px solid rgba(0, 0, 0, 0.08) !important;
	background: #fafafa !important;
	overflow: visible;
	transition: box-shadow 0.18s ease, transform 0.18s ease !important;

	&:hover {
		transform: translateY(-1px);
	}
}

.media-preview-avatar {
	background: rgba(76, 175, 80, 0.08) !important;
	border-radius: 10px !important;
}

.media-preview-dropzone {
	position: relative;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	min-width: 160px;
	min-height: 132px;
	margin: 0 12px 12px 0;
	padding: 1rem 1.25rem;
	border: 2px dashed rgba(76, 175, 80, 0.4);
	border-radius: 12px;
	background: rgba(76, 175, 80, 0.03);
	cursor: pointer;
	transition:
		border-color 0.2s ease,
		background 0.2s ease,
		transform 0.15s ease,
		box-shadow 0.2s ease;

	&:hover {
		border-color: rgba(76, 175, 80, 0.7);
		background: rgba(76, 175, 80, 0.07);
		transform: translateY(-2px);
		box-shadow: 0 6px 20px rgba(76, 175, 80, 0.12);
	}

	&:active {
		transform: translateY(0);
		box-shadow: none;
	}

	/* keyboard focus ring — the input inside is visually hidden but focusable */
	&:focus-within {
		border-color: rgba(76, 175, 80, 0.7);
		background: rgba(76, 175, 80, 0.07);
		box-shadow: 0 0 0 3px rgba(76, 175, 80, 0.25);
	}
}

/* visually hidden but still focusable and keyboard-operable,
   unlike the `hidden` attribute which removes it from the tab order */
.media-preview-dropzone__input {
	position: absolute;
	width: 1px;
	height: 1px;
	opacity: 0;
	overflow: hidden;
}

.media-preview-dropzone__title {
	font-size: 0.875rem;
	font-weight: 600;
	color: rgba(0, 0, 0, 0.72);
	margin-top: 0.25rem;
}

.media-preview-dropzone__sub {
	font-size: 0.6875rem;
	color: rgba(0, 0, 0, 0.4);
	text-align: center;
	line-height: 1.4;
	margin-top: 0.3rem;
	max-width: 140px;
}
</style>
